import { computed } from 'vue';
import type { DatePickerProps, DatePickerEmits } from '../types/datePicker.types';
import { DateTimeUtil } from '../utils/dateTimeUtil';

export function useDatePicker(props: DatePickerProps, emit: DatePickerEmits) {
  const computedFormat = computed(() => {
    if (props.format) {
      return props.format;
    }

    if (props.useDefaultFormat) {
      switch (props.type) {
        case 'date':
        case 'daterange':
          return 'dd/MM/yyyy';
        default:
          return undefined;
      }
    }

    return undefined;
  });

  const computedValueFormat = computed(() => {
    if (props.valueFormat) {
      return props.valueFormat;
    }

    if (props.useDefaultValueFormat) {
      switch (props.type) {
        case 'date':
        case 'daterange':
          return 'yyyy-MM-dd';
        default:
          return undefined;
      }
    }

    return undefined;
  });

  const computedPickerOptions = computed(() => {
    const baseOptions: Record<string, any> = {
      firstDayOfWeek: props.firstDayOfWeek || 1,
      ...props.pickerOptions,
    };
    if (props.minDate || props.maxDate || props.disabledDates) {
      baseOptions.disabledDate = (time: Date) => {
        if (props.disabledDates && props.disabledDates(time)) {
          return true;
        }
        if (props.minDate) {
          const minDate = DateTimeUtil.getStartOf(props.minDate, 'day');
          if (DateTimeUtil.isSameOrAfter(minDate, time)) {
            return true;
          }
        }
        if (props.maxDate) {
          const maxDate = DateTimeUtil.getEndOf(props.maxDate, 'day');
          if (DateTimeUtil.isSameOrBefore(maxDate, time)) {
            return true;
          }
        }
        return false;
      };
    }
    return baseOptions;
  });

  const handleChange = (value: string | Date | Array<string | Date> | null) => {
    emit('change', value);
  };

  const handleBlur = (event: FocusEvent) => {
    emit('blur', event);
  };

  const handleFocus = (event: FocusEvent) => {
    emit('focus', event);
  };

  const handleCalendarChange = (value: string | Date | Array<string | Date> | null) => {
    emit('calendar-change', value);
  };

  const handleVisibleChange = (visible: boolean) => {
    emit('visible-change', visible);
  };

  const handlePanelChange = (value: string | Date | Array<string | Date> | null) => {
    emit('panel-change', value);
  };

  return {
    computedFormat,
    computedValueFormat,
    computedPickerOptions,
    handleChange,
    handleBlur,
    handleFocus,
    handleCalendarChange,
    handleVisibleChange,
    handlePanelChange,
  };
}
